import {ParseBlockedUsersCookiesError} from '../Errors/ParseBlockedUsersCookiesError.js'
import {BlockedUsersCookiesValueObject} from '../ValueObjects/BlockedUsersCookiesValueObject.js'
import UserRepository from './UserRepository.js'

/**
 * @class
 * @implements UserRepository
 */
export default class CookieUserCochesRepository extends UserRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Function} deps.jsCookie
   * @param {Config} deps.config
   * @param {userEntityFactory} deps.userEntityFactory
   * @param {Function} deps.log
   * @param {Function} deps.jsCookie
   * @param {BlockedUsersCookiesValueObject} deps.blockedUsersCookieValueObject
   */
  constructor({config, userEntityFactory, jsCookie}) {
    super({
      config,
      userEntityFactory,
      jsCookie
    })
    this._config = config
    this._userEntityFactory = userEntityFactory
    this._jsCookie = jsCookie
    this._LOGIN_COOKIE_NAME = this._config.get('login').LOGIN_COOKIE_NAME
    this._LEGACY_LOGIN_COOKIE_NAME = this._config.get('login').LEGACY_LOGIN_COOKIE_NAME
    this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS = this._config.get('login').LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS
    this._SECURE_COOKIES = this._config.get('SECURE_COOKIES')
    this._BLOCKED_USERS_COOKIE = this._config.get('COMMON').COOKIES.BLOCKED_USERS_KEY
  }

  _cleanUndefinedProperties(object) {
    return JSON.parse(JSON.stringify(object))
  }

  _getUserData(cookie) {
    return cookie && cookie.UserData && JSON.parse(cookie.UserData)
  }

  _getLogUserData(cookie) {
    return cookie && cookie.LogUser && JSON.parse(cookie.LogUser)
  }

  _getUserDataFromCookies() {
    const userData = this._jsCookie.get('UserData')
    return userData && JSON.parse(userData)
  }

  _getLogUserFromCookies() {
    const logUser = this._jsCookie.get('LogUser')
    return logUser && JSON.parse(logUser)
  }

  _updateUserData({cookie, newUserData}) {
    const cookieUserData = this._getUserData(cookie)
    const cleanNewUserData = this._cleanUndefinedProperties(newUserData)
    const params = {...cookieUserData, ...cleanNewUserData}

    cookie[this._LOGIN_COOKIE_NAME] = JSON.stringify(params)
    this._jsCookie.set(this._LOGIN_COOKIE_NAME, params, {
      expires: this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS,
      secure: this._SECURE_COOKIES
    })
  }

  /**
   * @return {UserEntity}
   */
  getCurrentUser() {
    const cookie = this._config.get('cookie')
    let userData, logUserData

    try {
      userData = this._getUserData(cookie)
      logUserData = this._getLogUserData(cookie)
    } catch (e) {}

    return Promise.resolve(
      userData &&
        logUserData &&
        this._userEntityFactory({
          lastName: userData.lastName,
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          userId: userData.userid,
          avatar: userData.avatar,
          token: logUserData.token,
          isEmailConfirmed: userData.isEmailConfirmed,
          province: userData.provinceID ? String(userData.provinceID) : null,
          provinceId: userData.provinceID ? String(userData.provinceID) : null,
          zipCode: userData.zipCode,
          hidePhone: userData.hidePhone
        })
    )
  }

  /**
   * @param {Request} updateCurrentUserRequest
   */
  updateCurrentUser({shouldReadUpdatedVO, updateCurrentUserRequest}) {
    const cookie = this._config.get('cookie')
    const newUserData = updateCurrentUserRequest.toJSON()
    this._updateUserData({cookie, newUserData: shouldReadUpdatedVO ? newUserData.userData : newUserData})

    return this.getCurrentUser()
  }

  hasCookie(cookieName) {
    return Boolean(this._jsCookie.get(cookieName))
  }

  async updateUser() {
    const cookieUserData = this._getUserDataFromCookies()
    const cookieLogUser = this._getLogUserFromCookies()
    const cookie = this._config.get('cookie')
    this._config.set('cookie', {
      ...cookie,
      UserData: JSON.stringify(cookieUserData),
      LogUser: JSON.stringify(cookieLogUser)
    })
  }

  setAuthenticationToken({authenticationToken, userId}) {
    this._jsCookie.set(`authenticationToken_${userId}`, authenticationToken, {
      expires: this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS,
      secure: this._SECURE_COOKIES
    })
    return Promise.resolve()
  }

  setToken({token}) {
    const logUser = this._getLogUserFromCookies() || {}
    this._jsCookie.set(
      this._LEGACY_LOGIN_COOKIE_NAME,
      {...logUser, token},
      {
        expires: this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS,
        secure: this._SECURE_COOKIES
      }
    )
    const userData = this._getUserDataFromCookies() || {}
    this._jsCookie.set(
      this._LOGIN_COOKIE_NAME,
      {...userData, token},
      {
        expires: this._LOGIN_COOKIE_EXPIRATION_TIME_IN_DAYS,
        secure: this._SECURE_COOKIES
      }
    )
    return Promise.resolve()
  }

  getBlockedUsers() {
    try {
      let blockedCookieValue = []
      const cookieValue = this._jsCookie.get(this._BLOCKED_USERS_COOKIE)
      if (cookieValue === undefined || cookieValue === null || cookieValue === '' || cookieValue === 'undefined') {
        return BlockedUsersCookiesValueObject.createFromCookie({blockedUsersParsedCookie: blockedCookieValue})
      }

      if (typeof cookieValue === 'string' && cookieValue.trim() === '') {
        return BlockedUsersCookiesValueObject.createFromCookie({blockedUsersParsedCookie: blockedCookieValue})
      }

      const parsedCookie = JSON.parse(cookieValue)

      if (Array.isArray(parsedCookie)) {
        blockedCookieValue = parsedCookie
        return BlockedUsersCookiesValueObject.createFromCookie({blockedUsersParsedCookie: blockedCookieValue})
      }

      return BlockedUsersCookiesValueObject.createFromCookie({blockedUsersParsedCookie: blockedCookieValue})
    } catch (error) {
      throw ParseBlockedUsersCookiesError.create({
        message: `Failed to parse blocked users cookies: ${error.message}`
      })
    }
  }
}
